import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Lock,
  RefreshCw,
  Send,
} from "lucide-react";
import { useBuckets } from "./shared/useBuckets";
import { SettlementDrawer } from "./shared/SettlementDrawer";
import { MarkTransferredModal } from "./ToTransferScreen";
import { settlementsApi } from "./api";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { fmtCLP } from "../dashboard/format";
import { SLA_COLOR_MAP, formatDateShort } from "./shared/labels";
import type { HistoryRow, PendingTransferRow, SinLiquidarRow } from "./types";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { cn } from "@/lib/utils/cn";

/**
 * Tablero Kanban del flujo de liquidación — v1 con BOTONES, sin drag & drop.
 *
 * Reglas (por qué no es un Trello libre):
 * 1. Las transiciones son las de la state machine y SIEMPRE pasan por su
 *    modal/confirmación (comprobantes, motivo de falla, aviso de email).
 * 2. "En revisión" es turno del PRODUCTOR (acepta/disputa desde su Hub o
 *    tácita a 3 días): cards read-only con countdown.
 * 3. "Sin liquidar" parte de Events (no de Settlement): hace visibles los
 *    eventos terminados cuya liquidación aún no existe.
 * 4. "Transferida" se capa a las últimas 10 — el histórico completo vive
 *    en su propia vista.
 *
 * Mismo payload que las listas (dashboard-buckets + bucket sinLiquidar);
 * las sub-tabs de lista siguen existiendo para operar denso.
 */

const CAP_TRANSFERIDAS = 10;

export function BoardScreen() {
  const query = useBuckets();
  const qc = useQueryClient();
  const { push } = useToast();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [transferModal, setTransferModal] = useState<{
    settlementId: number;
    amount: number;
    paymentInfo?: PendingTransferRow["paymentInfo"];
  } | null>(null);

  const cols = useMemo(() => {
    const b = query.data?.buckets;
    const historico = b?.historico ?? [];
    return {
      sinLiquidar: b?.sinLiquidar ?? [],
      enRevision: b?.pendientesRevision ?? [],
      disputas: b?.disputasAbiertas ?? [],
      // Las FALLIDAS requieren acción (reintento) → viven en la bandeja de
      // acción del admin, no escondidas en el histórico.
      porTransferir: [
        ...(b?.pendientesTransferencia ?? []),
        ...historico.filter((r) => r.status === "TRANSFERENCIA_FALLIDA"),
      ],
      enTransferencia: b?.enTransferencia ?? [],
      transferidas: historico.filter((r) => r.status === "TRANSFERIDA"),
    };
  }, [query.data]);

  // ── Mutations ──────────────────────────────────────────────────────
  const invalidate = () => qc.invalidateQueries({ queryKey: ["settlements"] });

  const generar = useMutation({
    mutationFn: (eventId: number) => settlementsApi.finalizeEvent(eventId),
    onSuccess: () => {
      push({ kind: "success", message: "Liquidación generada. Email enviado al productor; ventana de 3 días corriendo." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || err?.response?.data?.message || "Error al generar la liquidación." }),
  });

  const iniciarTransferencia = useMutation({
    mutationFn: (id: number) => settlementsApi.markPendingTransfer(id),
    onSuccess: () => {
      push({ kind: "success", message: "Marcada en transferencia." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || err?.response?.data?.message || "Error al iniciar la transferencia." }),
  });

  const markFailed = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      settlementsApi.markTransferFailed(id, reason),
    onSuccess: () => {
      push({ kind: "success", message: "Marcada como fallida. Alerta enviada a soporte." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || "Error al marcar fallida." }),
  });

  // ── Handlers con confirmación ──────────────────────────────────────
  const handleGenerar = async (row: SinLiquidarRow) => {
    const ok = await confirmAction({
      title: `Generar liquidación de "${row.eventName}"`,
      message:
        "Se calcula la liquidación (misma vía que el cron), se envía el email al productor y arranca la ventana de revisión de 3 días. Los tickets pagados no acreditados se expiran.",
      confirmLabel: "Generar y notificar",
    });
    if (ok !== null && ok !== false) generar.mutate(row.eventId);
  };

  const handleIniciarTransferencia = async (id: number, retry = false) => {
    const ok = await confirmAction({
      title: retry
        ? `Reintentar transferencia #${id}`
        : `Iniciar transferencia #${id}`,
      message: retry
        ? "La liquidación fallida vuelve a 'En transferencia' para un nuevo intento."
        : "Marca que la orden ya fue dada al banco. Al confirmar el abono, usa 'Marcar transferida' con el comprobante.",
      confirmLabel: retry ? "Reintentar" : "Iniciar",
    });
    if (ok !== null && ok !== false) iniciarTransferencia.mutate(id);
  };

  const handleMarkFailed = async (id: number) => {
    const reason = await confirmAction({
      title: `Marcar transferencia #${id} como fallida`,
      promptLabel: "Motivo (obligatorio — Cuenta destino inactiva, RUT incorrecto, etc.)",
      confirmLabel: "Marcar fallida",
      tone: "danger",
    });
    if (reason === null || reason === false) return; // canceló
    if (typeof reason === "string" && reason.trim()) {
      markFailed.mutate({ id, reason: reason.trim() });
    } else {
      // Confirmó con el motivo vacío: no-op silencioso = falso-hecho peligroso.
      push({ kind: "error", message: "El motivo es obligatorio — no se marcó como fallida." });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Liquidaciones · Tablero</p>
          <h1 className="nx-h1">Tablero de liquidaciones</h1>
          <p className="text-sm text-fg-muted mt-1">
            Todo el ciclo en una vista: del evento terminado a la plata transferida. Una card = una liquidación.
          </p>
        </div>
        <button
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="nx-btn-secondary"
        >
          <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      {query.isLoading ? (
        <LoadingBlock label="Cargando tablero…" />
      ) : query.isError ? (
        <ErrorBlock onRetry={() => query.refetch()} />
      ) : (
        <div className="flex items-start gap-3 overflow-x-auto pb-2">
          {/* ── COL 0 · SIN LIQUIDAR ── */}
          <BoardColumn
            dotClass="bg-warning"
            title="Sin liquidar"
            count={cols.sinLiquidar.length}
            subtitle="Eventos terminados sin liquidación"
            footer="Generar corre la misma vía que el cron: email al productor + ventana de 3 días."
          >
            {cols.sinLiquidar.length === 0 ? (
              <EmptyCol label="Nada pendiente de generar" />
            ) : (
              cols.sinLiquidar.map((r) => (
                <article key={r.eventId} className="rounded-lg border border-border bg-bg-card p-3 shadow-nx-sm flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-2xs text-fg-subtle">
                    <span className="font-semibold">EV #{r.eventId}</span>
                    <span className="ml-auto">{formatDateShort(r.eventDate)}</span>
                  </div>
                  <div className="text-sm font-medium text-fg leading-snug">{r.eventName}</div>
                  <div className="text-2xs text-fg-muted">{r.productorNombre || r.productorEmail || "—"}</div>
                  <button
                    className="nx-btn-primary justify-center text-xs"
                    disabled={generar.isPending}
                    onClick={() => handleGenerar(r)}
                  >
                    Generar liquidación
                  </button>
                </article>
              ))
            )}
          </BoardColumn>

          {/* ── COL 1 · EN REVISIÓN (turno del productor) ── */}
          <BoardColumn
            dotClass="bg-warning"
            title="En revisión"
            count={cols.enRevision.length}
            subtitle={
              <span className="inline-flex items-center gap-1">
                <Lock size={10} /> Turno del productor — solo lectura
              </span>
            }
            footer="El productor acepta o disputa desde su Hub; a los 3 días acepta tácita y la card avanza sola."
          >
            {cols.enRevision.length === 0 ? (
              <EmptyCol label="Sin liquidaciones en revisión" />
            ) : (
              cols.enRevision.map((r) => (
                <article
                  key={r.settlementId}
                  onClick={() => setSelectedId(r.settlementId)}
                  className="rounded-lg border border-border bg-bg-card p-3 shadow-nx-sm flex flex-col gap-2 cursor-pointer hover:shadow-nx-md transition-shadow"
                >
                  <div className="flex items-center gap-2 text-2xs">
                    <span className="font-semibold text-fg-subtle">#{r.settlementId}</span>
                    <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-warning-soft px-1.5 py-0.5 font-semibold text-warning">
                      <Clock size={10} /> {deadlineLabel(r.revisionDeadlineAt)}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-fg leading-snug">{r.eventName}</div>
                  <div className="text-2xs text-fg-muted">{r.productorNombre || r.productorEmail || "—"}</div>
                  <div className="text-base font-bold text-fg tabular-nums">{fmtCLP(r.amountToTransfer)}</div>
                </article>
              ))
            )}
          </BoardColumn>

          {/* ── COL 2 · EN DISPUTA ── */}
          <BoardColumn
            dotClass="bg-danger"
            title="En disputa"
            count={cols.disputas.length}
            subtitle="Requiere resolución del admin"
          >
            {cols.disputas.length === 0 ? (
              <EmptyCol label="Sin disputas abiertas" />
            ) : (
              cols.disputas.map((r) => (
                <article
                  key={r.settlementId}
                  onClick={() => setSelectedId(r.settlementId)}
                  className="rounded-lg border border-danger bg-bg-card p-3 shadow-nx-sm flex flex-col gap-2 cursor-pointer hover:shadow-nx-md transition-shadow"
                >
                  <div className="flex items-center gap-2 text-2xs">
                    <span className="font-semibold text-fg-subtle">#{r.settlementId}</span>
                    <span className="ml-auto text-fg-subtle">{formatDateShort(r.disputedAt)}</span>
                  </div>
                  <div className="text-sm font-medium text-fg leading-snug">{r.eventName}</div>
                  <div className="text-2xs text-fg-muted">{r.productorNombre || r.productorEmail || "—"}</div>
                  <div className="text-base font-bold text-fg tabular-nums">{fmtCLP(r.amountToTransfer)}</div>
                  <Link
                    to="/settlements/disputes"
                    onClick={(e) => e.stopPropagation()}
                    className="nx-btn-secondary justify-center text-xs"
                  >
                    Resolver disputa <ArrowRight size={12} />
                  </Link>
                </article>
              ))
            )}
          </BoardColumn>

          {/* ── COL 3 · POR TRANSFERIR (bandeja de acción) ── */}
          <BoardColumn
            dotClass="bg-accent"
            title="Por transferir"
            count={cols.porTransferir.length}
            subtitle="Aceptadas + resueltas + fallidas (reintento)"
            highlight
            footer="Marcar transferida exige comprobante. Sin datos bancarios del productor, queda bloqueado."
          >
            {cols.porTransferir.length === 0 ? (
              <EmptyCol label="Nada por transferir" />
            ) : (
              cols.porTransferir.map((r) =>
                r.status === "TRANSFERENCIA_FALLIDA" ? (
                  <FailedCard
                    key={`h-${r.settlementId}`}
                    row={r as HistoryRow}
                    onOpen={() => setSelectedId(r.settlementId)}
                    onRetry={() => handleIniciarTransferencia(r.settlementId, true)}
                  />
                ) : (
                  <TransferCard
                    key={r.settlementId}
                    row={r as PendingTransferRow}
                    onOpen={() => setSelectedId(r.settlementId)}
                    onTransfer={() =>
                      setTransferModal({
                        settlementId: r.settlementId,
                        amount: r.amountToTransfer,
                        paymentInfo: (r as PendingTransferRow).paymentInfo,
                      })
                    }
                    onStart={() => handleIniciarTransferencia(r.settlementId)}
                  />
                ),
              )
            )}
          </BoardColumn>

          {/* ── COL 4 · EN TRANSFERENCIA ── */}
          <BoardColumn
            dotClass="bg-info"
            title="En transferencia"
            count={cols.enTransferencia.length}
            subtitle="Orden dada al banco, esperando confirmación"
          >
            {cols.enTransferencia.length === 0 ? (
              <EmptyCol label="Ninguna orden en curso" />
            ) : (
              cols.enTransferencia.map((r) => (
                <article
                  key={r.settlementId}
                  onClick={() => setSelectedId(r.settlementId)}
                  className="rounded-lg border border-border bg-bg-card p-3 shadow-nx-sm flex flex-col gap-2 cursor-pointer hover:shadow-nx-md transition-shadow"
                >
                  <div className="flex items-center gap-2 text-2xs">
                    <span className="font-semibold text-fg-subtle">#{r.settlementId}</span>
                    <span className="ml-auto text-fg-subtle">desde {formatDateShort(r.transferStartedAt)}</span>
                  </div>
                  <div className="text-sm font-medium text-fg leading-snug">{r.eventName}</div>
                  <div className="text-2xs text-fg-muted">{r.productorNombre || r.productorEmail || "—"}</div>
                  <div className="text-base font-bold text-fg tabular-nums">{fmtCLP(r.amountToTransfer)}</div>
                  <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="nx-btn-primary flex-1 justify-center text-xs"
                      onClick={() =>
                        setTransferModal({
                          settlementId: r.settlementId,
                          amount: r.amountToTransfer,
                          paymentInfo: r.paymentInfo,
                        })
                      }
                    >
                      Marcar transferida
                    </button>
                    <button
                      className="nx-btn-secondary justify-center text-xs"
                      onClick={() => handleMarkFailed(r.settlementId)}
                    >
                      Falló
                    </button>
                  </div>
                </article>
              ))
            )}
          </BoardColumn>

          {/* ── COL 5 · TRANSFERIDA ── */}
          <BoardColumn
            dotClass="bg-success"
            title="Transferida"
            count={cols.transferidas.length}
            subtitle={
              <span>
                Últimas {Math.min(CAP_TRANSFERIDAS, cols.transferidas.length)} ·{" "}
                <Link to="/settlements/history" className="text-accent font-semibold hover:underline">
                  histórico completo →
                </Link>
              </span>
            }
          >
            {cols.transferidas.length === 0 ? (
              <EmptyCol label="Aún no hay transferencias" />
            ) : (
              cols.transferidas.slice(0, CAP_TRANSFERIDAS).map((r) => (
                <article
                  key={r.settlementId}
                  onClick={() => setSelectedId(r.settlementId)}
                  className="rounded-lg border border-border bg-bg-card p-3 shadow-nx-sm flex flex-col gap-1.5 cursor-pointer hover:shadow-nx-md transition-shadow"
                >
                  <div className="flex items-center gap-2 text-2xs">
                    <span className="font-semibold text-fg-subtle">#{r.settlementId}</span>
                    <span className="ml-auto text-fg-subtle">{formatDateShort(r.transferredAt)}</span>
                  </div>
                  <div className="text-xs font-medium text-fg leading-snug">{r.eventName}</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-bold text-success tabular-nums">{fmtCLP(r.amountToTransfer)}</span>
                    <span className="text-2xs text-fg-subtle">{r.productorNombre || "—"}</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-2xs text-fg-subtle">
                    <CheckCircle2 size={10} /> comprobante adjunto
                  </span>
                </article>
              ))
            )}
          </BoardColumn>
        </div>
      )}

      <SettlementDrawer settlementId={selectedId} onClose={() => setSelectedId(null)} />

      {transferModal && (
        <MarkTransferredModal
          settlementId={transferModal.settlementId}
          amount={transferModal.amount}
          paymentInfo={transferModal.paymentInfo}
          onClose={() => setTransferModal(null)}
          onDone={() => {
            setTransferModal(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

// ── Piezas del tablero ─────────────────────────────────────────────────

function BoardColumn({
  dotClass,
  title,
  count,
  subtitle,
  footer,
  highlight,
  children,
}: {
  dotClass: string;
  title: string;
  count: number;
  subtitle?: React.ReactNode;
  footer?: string;
  highlight?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        "w-[250px] shrink-0 rounded-xl border bg-bg-subtle flex flex-col relative",
        highlight ? "border-accent shadow-[0_0_0_3px_var(--nx-accent-glow)]" : "border-border",
      )}
    >
      {highlight && (
        <span className="absolute -top-2 left-3 rounded-md bg-accent px-2 py-0.5 text-[10px] font-bold tracking-wide text-fg-inverted">
          TU BANDEJA DE ACCIÓN
        </span>
      )}
      <header className="flex flex-col gap-0.5 px-3.5 pt-3.5 pb-2.5">
        <div className="flex items-center gap-2">
          <span className={cn("h-2 w-2 rounded-full", dotClass)} />
          <span className="text-[13px] font-bold text-fg">{title}</span>
          <span className="ml-auto rounded-full border border-border bg-bg-card px-2 py-px text-[11px] font-bold text-fg-muted tabular-nums">
            {count}
          </span>
        </div>
        {subtitle && <span className="text-2xs text-fg-subtle">{subtitle}</span>}
      </header>
      <div className="flex max-h-[560px] flex-col gap-2 overflow-y-auto px-2.5 pb-2.5">
        {children}
      </div>
      {footer && (
        <footer className="border-t border-dashed border-border px-3.5 py-2 text-[10.5px] leading-snug text-fg-subtle">
          {footer}
        </footer>
      )}
    </section>
  );
}

function EmptyCol({ label }: { label: string }) {
  return <div className="py-6 text-center text-xs text-fg-subtle">{label}</div>;
}

function TransferCard({
  row,
  onOpen,
  onTransfer,
  onStart,
}: {
  row: PendingTransferRow;
  onOpen: () => void;
  onTransfer: () => void;
  onStart: () => void;
}) {
  const bankOk = row.paymentInfo?.isComplete;
  return (
    <article
      onClick={onOpen}
      className="rounded-lg border border-border bg-bg-card p-3 shadow-nx-sm flex flex-col gap-2 cursor-pointer hover:shadow-nx-md transition-shadow"
    >
      <div className="flex items-center gap-2 text-2xs">
        <span className="font-semibold text-fg-subtle">#{row.settlementId}</span>
        {row.sourceBadge === "post_dispute" && (
          <span className="rounded-md bg-accent-soft px-1.5 py-0.5 font-semibold text-accent">post-disputa</span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-danger-soft px-1.5 py-0.5 font-bold text-danger">
          <span className={cn("h-1.5 w-1.5 rounded-full", SLA_COLOR_MAP[row.slaColor])} />
          SLA · {row.daysPending}d
        </span>
      </div>
      <div className="text-sm font-medium text-fg leading-snug">{row.eventName}</div>
      <div className="text-2xs text-fg-muted">{row.productorNombre || row.productorEmail || "—"}</div>
      <div className="text-base font-bold text-fg tabular-nums">{fmtCLP(row.amountToTransfer)}</div>
      {!bankOk && (
        <span className="inline-flex w-fit items-center gap-1 rounded-md bg-danger-soft px-1.5 py-0.5 text-2xs font-semibold text-danger">
          <AlertTriangle size={10} /> datos bancarios incompletos
        </span>
      )}
      <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
        <button
          className="nx-btn-primary flex-1 justify-center text-xs disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!bankOk}
          title={bankOk ? undefined : "Bloqueado: faltan datos bancarios del productor"}
          onClick={onTransfer}
        >
          Marcar transferida
        </button>
        <button
          className="nx-btn-secondary justify-center text-xs disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!bankOk}
          title={
            bankOk
              ? "Marcar que la orden ya fue dada al banco"
              : "Bloqueado: faltan datos bancarios del productor"
          }
          onClick={onStart}
        >
          <Send size={12} />
        </button>
      </div>
    </article>
  );
}

function FailedCard({
  row,
  onOpen,
  onRetry,
}: {
  row: HistoryRow;
  onOpen: () => void;
  onRetry: () => void;
}) {
  return (
    <article
      onClick={onOpen}
      className="rounded-lg border border-danger bg-bg-card p-3 shadow-nx-sm flex flex-col gap-2 cursor-pointer hover:shadow-nx-md transition-shadow"
    >
      <div className="flex items-center gap-2 text-2xs">
        <span className="font-semibold text-fg-subtle">#{row.settlementId}</span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-danger-soft px-1.5 py-0.5 font-bold text-danger">
          <AlertTriangle size={10} /> fallida
        </span>
      </div>
      <div className="text-sm font-medium text-fg leading-snug">{row.eventName}</div>
      <div className="text-2xs text-fg-muted">{row.productorNombre || row.productorEmail || "—"}</div>
      <div className="text-base font-bold text-fg tabular-nums">{fmtCLP(row.amountToTransfer)}</div>
      {row.transferFailureReason && (
        <div className="text-2xs leading-snug text-danger">{row.transferFailureReason}</div>
      )}
      <button
        className="nx-btn-secondary justify-center text-xs"
        onClick={(e) => {
          e.stopPropagation();
          onRetry();
        }}
      >
        Reintentar transferencia
      </button>
    </article>
  );
}

/** "tácita en 2d 5h" / "vence hoy" / "vencida" para la ventana de revisión. */
function deadlineLabel(iso: string | null): string {
  if (!iso) return "sin deadline";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "vencida — tácita inminente";
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  if (days === 0 && hours === 0) return "vence en minutos";
  if (days === 0) return `tácita en ${hours}h`;
  return `tácita en ${days}d ${hours}h`;
}
